import { formatDate, MOMENT_FORMATS, parseEventDateTime, toMoment } from '../../utils/dateFormat';

export const ARIA_MESSAGE_TYPES = Object.freeze({
  WELCOME: 'first_contact_welcome',
  ONBOARDING_PREVIEW: 'first_contact_onboarding_preview',
  CALIBRATION: 'first_contact_calibration',
  EVENT_RECOMMANDATION: 'event_recommendation',
  FIRST_CONTACT_REENGAGEMENT: 'first_contact_reengagement',
  PEOPLE_TO_MEET: 'people_to_meet',
  POST_EVENT_CHECKIN: 'post_event_checkin',
  POST_EVENT_SHARE_PROMPT: 'post_event_share_prompt',
});

export const ARIA_PEOPLE_DECLINE_REASONS = Object.freeze([
  'Age gap',
  'Too far',
  'Different vibe',
  'Already met',
  'Other',
]);

export const ARIA_CARD_ACCENT = '#d4726a';
export const ARIA_CARD_ACCENT_SOFT = '#f8ece9';

const toTrimmed = (value) => {
  const trimmed = String(value ?? '').trim();
  return trimmed || null;
};

/** Prefer Stream/API `data` object; ignore non-objects. */
export const getAriaMessageDataEnvelope = (message = {}) => {
  const candidates = [message?.data, message?.custom, message?.messageData];
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === 'string') {
      try {
        const parsed = JSON.parse(candidate);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // ignore non-JSON strings
      }
      continue;
    }
    if (typeof candidate === 'object' && !Array.isArray(candidate)) {
      return candidate;
    }
  }
  return null;
};

export const getAriaMessageType = (messageOrData = {}) => {
  const envelope =
    messageOrData?.ariaMessageType != null
      ? messageOrData
      : getAriaMessageDataEnvelope(messageOrData);
  return toTrimmed(envelope?.ariaMessageType)?.toLowerCase() ?? null;
};

/** Mock-style date line: "Thu · 7:30 PM". Falls back to full date when time missing. */
export const formatAriaEventDateLine = (eventDate, startTime) => {
  const date = parseEventDateTime(eventDate, startTime);
  if (!date) return null;

  if (toTrimmed(startTime) || String(eventDate ?? '').includes('T')) {
    return `${formatDate(date, 'ddd')} · ${formatDate(date, MOMENT_FORMATS.TIME)}`;
  }

  return formatDate(date, MOMENT_FORMATS.DATE_LONG);
};

export const formatAriaEventLocation = ({ eventFormat, venueName, location } = {}) => {
  if (
    String(eventFormat ?? '')
      .trim()
      .toLowerCase() === 'online'
  ) {
    return 'Online Event';
  }

  const venue = toTrimmed(venueName);
  if (venue) return venue;

  const loc = toTrimmed(location);
  if (!loc) return null;
  return loc.split('-')[0].trim() || loc;
};

export const formatAriaEventPrice = (price, currency = 'SS') => {
  if (price == null || price === '') return null;
  const currencyLabel = toTrimmed(currency) || 'SS';
  return `${price} ${currencyLabel}`;
};

export const normalizeAriaEventRecommendation = (data = {}) => {
  const eventId = toTrimmed(data.eventId) ?? toTrimmed(data.id);
  if (!eventId) return null;

  const seatsRaw = data.seatsRemaining;
  const seatsRemaining = seatsRaw == null || seatsRaw === '' ? null : Number(seatsRaw);

  return {
    ariaMessageType: ARIA_MESSAGE_TYPES.EVENT_RECOMMANDATION,
    eventId,
    title: toTrimmed(data.title) || 'Recommended event',
    coverImageUrl: toTrimmed(data.coverImageUrl),
    seatsRemaining: Number.isFinite(seatsRemaining) ? seatsRemaining : null,
    eventDate: toTrimmed(data.eventDate),
    startTime: toTrimmed(data.startTime),
    eventFormat: toTrimmed(data.eventFormat),
    venueName: toTrimmed(data.venueName),
    location: toTrimmed(data.location),
    whyThisOne: toTrimmed(data.whyThisOne),
    price: data.price ?? null,
    currency: toTrimmed(data.currency) || 'SS',
    bookingStatus: toTrimmed(data.bookingStatus)?.toLowerCase() ?? null,
    upcomingEvent: Boolean(data.upcomingEvent),
    type: data.type ?? null,
    wingmanDescription: data.wingmanDescription ?? null,
    bookedUsers: data.bookedUsers ?? null,
    dateLine: formatAriaEventDateLine(data.eventDate, data.startTime),
    locationLine: formatAriaEventLocation(data),
    priceLine: formatAriaEventPrice(data.price, data.currency),
    isBooked: String(data.bookingStatus ?? '').toLowerCase() === 'paid',
  };
};

export const normalizeAriaPeopleCandidate = (candidate = {}, index = 0) => {
  const userId = toTrimmed(candidate.userId);
  const uid = toTrimmed(candidate.uid);
  if (!userId && !uid) return null;

  const status = toTrimmed(candidate.introductionStatus)?.toLowerCase() || 'pending';

  return {
    key: userId || uid || `candidate-${index}`,
    userId: userId || null,
    uid: uid || null,
    fullName: toTrimmed(candidate.fullName) || 'Member',
    avatarUrl: toTrimmed(candidate.avatarUrl),
    profession: toTrimmed(candidate.profession),
    sharedInterestLabels: Array.isArray(candidate.sharedInterestLabels)
      ? candidate.sharedInterestLabels.map((label) => toTrimmed(label)).filter(Boolean)
      : [],
    reason: toTrimmed(candidate.reason),
    introductionStatus: ['pending', 'declined', 'accepted'].includes(status) ? status : 'pending',
    declineReason: toTrimmed(candidate.declineReason),
  };
};

export const normalizeAriaPeopleToMeet = (data = {}) => {
  const candidates = Array.isArray(data.candidates)
    ? data.candidates.map(normalizeAriaPeopleCandidate).filter(Boolean)
    : [];

  return {
    ariaMessageType: ARIA_MESSAGE_TYPES.PEOPLE_TO_MEET,
    candidates,
  };
};

export const normalizeAriaPostEventCheckin = (data = {}) => {
  const eventId = toTrimmed(data.eventId);
  const eventTitle = toTrimmed(data.eventTitle);
  if (!eventId && !eventTitle) return null;

  const maxRating = Number(data.rating?.max ?? 5);
  const submittedRatingRaw = data.submittedRating;
  const submittedRating =
    submittedRatingRaw == null || submittedRatingRaw === '' ? null : Number(submittedRatingRaw);

  return {
    ariaMessageType: ARIA_MESSAGE_TYPES.POST_EVENT_CHECKIN,
    eventId,
    eventTitle: eventTitle || 'Event',
    coverImageUrl: toTrimmed(data.coverImageUrl),
    eventDate: toTrimmed(data.eventDate),
    submitted: Boolean(data.submitted),
    submittedRating: Number.isFinite(submittedRating) ? submittedRating : null,
    maxRating: Number.isFinite(maxRating) && maxRating > 0 ? maxRating : 5,
    submittedHighlight: toTrimmed(data.submittedHighlight),
    submittedTableMatchScore:
      data.submittedTableMatchScore == null || data.submittedTableMatchScore === ''
        ? null
        : Number(data.submittedTableMatchScore),
    highlights: Array.isArray(data.highlights) ? data.highlights : [],
    tableMatch: data.tableMatch ?? null,
  };
};

export const formatSharePromptEventDateLine = (eventDate) => {
  const raw = String(eventDate ?? '').trim();
  if (!raw) return null;

  const date = toMoment(raw);
  if (!date) return raw;

  const daysAgo = toMoment(new Date()).startOf('day').diff(date.clone().startOf('day'), 'days');
  if (daysAgo === 1) return 'last night';
  if (daysAgo === 0) return 'today';
  return formatDate(date, MOMENT_FORMATS.DATE_LONG);
};

export const normalizeAriaPostEventSharePrompt = (data = {}) => {
  const eventId = toTrimmed(data.eventId);
  const eventTitle = toTrimmed(data.eventTitle);
  if (!eventId && !eventTitle) return null;

  const sharedPostRaw = data.sharedPost ?? {};
  const shareRaw = data.share ?? {};
  const audiences = Array.isArray(shareRaw.audiences)
    ? shareRaw.audiences
        .map((audience) => ({
          key: toTrimmed(audience.key),
          title: toTrimmed(audience.title) || toTrimmed(audience.key),
          subtitle: toTrimmed(audience.subtitle),
        }))
        .filter((audience) => audience.key)
    : [];

  return {
    ariaMessageType: ARIA_MESSAGE_TYPES.POST_EVENT_SHARE_PROMPT,
    eventId,
    eventTitle: eventTitle || 'Event',
    eventDate: toTrimmed(data.eventDate),
    eventDateLine: formatSharePromptEventDateLine(data.eventDate),
    posted: Boolean(data.posted),
    sharedPost: {
      imageUrl: toTrimmed(sharedPostRaw.imageUrl),
      videoUrl: toTrimmed(sharedPostRaw.videoUrl),
      mediaType: toTrimmed(sharedPostRaw.mediaType),
      caption: toTrimmed(sharedPostRaw.caption),
      audience: toTrimmed(sharedPostRaw.audience),
      postedAt: toTrimmed(sharedPostRaw.postedAt),
      comicId: toTrimmed(sharedPostRaw.comicId),
    },
    audiences,
  };
};

export const normalizeAriaFirstContactCalibration = (data = {}) => {
  const suggestedAnswers = Array.isArray(data.suggestedAnswers)
    ? data.suggestedAnswers.map((answer) => toTrimmed(answer)).filter(Boolean)
    : [];
  const calibrationQuestion = toTrimmed(data.calibrationQuestion);

  if (!suggestedAnswers.length && !calibrationQuestion) {
    return null;
  }

  return {
    ariaMessageType: ARIA_MESSAGE_TYPES.CALIBRATION,
    calibrationQuestion,
    suggestedAnswers,
  };
};

export const normalizeAriaFirstContactOnboardingPreview = (data = {}) => {
  const previewChips = Array.isArray(data.previewChips)
    ? data.previewChips.map((chip) => toTrimmed(chip)).filter(Boolean)
    : [];
  const confirmationOptions = Array.isArray(data.confirmationOptions)
    ? data.confirmationOptions
        .map((option) => ({
          key: toTrimmed(option.key),
          title: toTrimmed(option.title) || toTrimmed(option.key),
        }))
        .filter((option) => option.key || option.title)
    : [];
  const confirmationQuestion = toTrimmed(data.confirmationQuestion);

  if (!previewChips.length && !confirmationOptions.length && !confirmationQuestion) {
    return null;
  }

  return {
    ariaMessageType: ARIA_MESSAGE_TYPES.ONBOARDING_PREVIEW,
    previewChips,
    confirmationQuestion,
    confirmationOptions,
    blocked: Boolean(data.blocked),
  };
};

/**
 * Normalize flat message.data into a typed Aria rich-message payload.
 * Returns null when type is missing/unsupported or required fields are absent.
 */
export const normalizeAriaRichMessage = (messageOrData = {}) => {
  const envelope =
    messageOrData?.ariaMessageType != null && typeof messageOrData === 'object'
      ? messageOrData
      : getAriaMessageDataEnvelope(messageOrData);

  if (!envelope) return null;

  const type = getAriaMessageType(envelope);
  if (type === ARIA_MESSAGE_TYPES.EVENT_RECOMMANDATION) {
    return normalizeAriaEventRecommendation(envelope);
  }
  if (type === ARIA_MESSAGE_TYPES.PEOPLE_TO_MEET) {
    const people = normalizeAriaPeopleToMeet(envelope);
    // Empty candidates → nothing to render as cards (caller may still show text).
    return people;
  }
  if (type === ARIA_MESSAGE_TYPES.POST_EVENT_CHECKIN) {
    return normalizeAriaPostEventCheckin(envelope);
  }
  if (type === ARIA_MESSAGE_TYPES.POST_EVENT_SHARE_PROMPT) {
    return normalizeAriaPostEventSharePrompt(envelope);
  }
  if (type === ARIA_MESSAGE_TYPES.ONBOARDING_PREVIEW) {
    return normalizeAriaFirstContactOnboardingPreview(envelope);
  }
  if (type === ARIA_MESSAGE_TYPES.CALIBRATION) {
    return normalizeAriaFirstContactCalibration(envelope);
  }

  return null;
};

/** Renders as a separate card block below the text bubble (event / people / share cards). */
export const isAriaRichCardMessage = (message = {}) => {
  const type = message?.ariaMessageType ?? getAriaMessageType(message);
  const payload = message?.ariaPayload;
  if (!type || !payload) return false;

  switch (type) {
    case ARIA_MESSAGE_TYPES.EVENT_RECOMMANDATION:
      return Boolean(payload.eventId);
    case ARIA_MESSAGE_TYPES.PEOPLE_TO_MEET:
      return Boolean(payload.candidates?.length);
    case ARIA_MESSAGE_TYPES.POST_EVENT_SHARE_PROMPT:
      return Boolean(payload.eventId || payload.eventTitle);
    case ARIA_MESSAGE_TYPES.CALIBRATION:
      return Boolean(payload.suggestedAnswers?.length);
    default:
      return false;
  }
};

/** Renders inside the message bubble (check-in, onboarding preview, etc.). */
export const isAriaInlineRichMessage = (message = {}) => {
  const type = message?.ariaMessageType ?? getAriaMessageType(message);
  const payload = message?.ariaPayload;
  if (!type || !payload) return false;

  switch (type) {
    case ARIA_MESSAGE_TYPES.POST_EVENT_CHECKIN:
      return Boolean(payload.eventId || payload.eventTitle);
    case ARIA_MESSAGE_TYPES.ONBOARDING_PREVIEW:
      return Boolean(
        payload.previewChips?.length ||
        payload.confirmationOptions?.length ||
        payload.confirmationQuestion,
      );
    default:
      return false;
  }
};
